package com.tovtalk.webcanvas

import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.ExperimentalComposeUiApi
import androidx.compose.ui.window.ComposeViewport
import com.russhwolf.settings.Settings
import com.tovtalk.data.local.TokenManager
import com.tovtalk.data.local.initTokenManagerWebPersistence
import com.tovtalk.data.local.syncTokenManagerFromStorageIfNeeded
import com.tovtalk.di.getKoin
import com.tovtalk.di.getSharedModules
import com.tovtalk.ui.TovTalkApp
import com.tovtalk.ui.preloadNextVideoUrl
import com.tovtalk.ui.platform.completeGoogleRedirectAndSaveTokensIfAny
import com.tovtalk.ui.theme.TovTalkWithHebrewFont
import com.tovtalk.utils.config.isFontDebugEnabled
import com.tovtalk.utils.deeplink.DeepLinkParser
import com.tovtalk.utils.deeplink.DeepLinkRoute
import kotlinx.browser.document
import kotlinx.browser.localStorage
import kotlinx.browser.window
import kotlinx.coroutines.MainScope
import kotlinx.coroutines.launch
import org.koin.core.context.startKoin

/** Viewport width below this: #root full width (phones, tablets, small laptops). Aligns with CSS --viewport-full-bleed. */
private const val FULL_VIEWPORT_BREAKPOINT_PX = 1024

@OptIn(ExperimentalComposeUiApi::class)
fun main() {
    startKoin {
        modules(getSharedModules())
    }
    TokenManager.init(getKoin().get<Settings>())
    initTokenManagerWebPersistence()
    syncTokenManagerFromStorageIfNeeded()

    // On session expiry, redirect to onboarding via URL (NavController handles it from there)
    TokenManager.onSessionExpired = {
        window.location.href = "/onboarding"
    }

    val root = document.getElementById("root")
        ?: error("#root element missing from index.html — required for ComposeViewport")
    document.getElementById("splash")?.let { it.parentNode?.removeChild(it) }

    // Consume any pending Google sign-in redirect result early and save backend tokens, then start the UI.
    MainScope().launch {
        try { completeGoogleRedirectAndSaveTokensIfAny() } catch (_: Throwable) { }
        startApp(root)
    }
}

@OptIn(ExperimentalComposeUiApi::class)
private fun startApp(root: org.w3c.dom.Element) {

    // CSS var --vvh = visible viewport height (mobile: accounts for address bar, keyboard)
    fun setVVH() {
        val vv = window.asDynamic().visualViewport
        val h = if (vv != null && (vv.height as Double) > 0) vv.height as Double
                else (window.asDynamic().innerHeight as Double)
        document.documentElement?.asDynamic()?.style?.setProperty("--vvh", "${kotlin.math.round(h)}px")
    }
    setVVH()
    val vv = window.asDynamic().visualViewport
    if (vv != null) {
        vv.addEventListener("resize", { _: org.w3c.dom.events.Event -> setVVH() })
        vv.addEventListener("scroll", { _: org.w3c.dom.events.Event -> setVVH() })
    }

    // When viewport is narrow or short: force #root full width/height so layout fits every device size.
    fun applyResponsiveRoot() {
        val w = (window.asDynamic().innerWidth as Double).toInt()
        val h = (window.asDynamic().innerHeight as Double).toInt()
        val fullViewport = w <= FULL_VIEWPORT_BREAKPOINT_PX || h <= 600
        val r = root as? org.w3c.dom.HTMLElement ?: return
        val s = r.asDynamic().style
        if (fullViewport) {
            s.width = "100%"
            s.maxWidth = "100%"
            s.minWidth = "0"
        } else {
            s.width = ""
            s.maxWidth = ""
            s.minWidth = ""
        }
    }
    applyResponsiveRoot()
    window.addEventListener("resize", { _: org.w3c.dom.events.Event ->
        setVVH()
        applyResponsiveRoot()
    })

    val lang = localStorage.getItem("app_language") ?: "en"
    val debugFont = isFontDebugEnabled()

    // Sync document dir/lang for RTL support
    val dir = if (lang == "he") "rtl" else "ltr"
    val langAttr = if (lang == "he") "he" else "en"
    document.documentElement?.setAttribute("dir", dir)
    document.documentElement?.setAttribute("lang", langAttr)
    root.setAttribute("dir", dir)
    root.setAttribute("lang", langAttr)

    ComposeViewport(root) {
        val initialUrl = remember {
            "${window.location.origin}${window.location.pathname}${window.location.search ?: ""}"
        }
        var pendingDeepLink by remember { mutableStateOf<DeepLinkRoute?>(DeepLinkParser.parse(initialUrl)) }
        var isAppInForeground by remember {
            mutableStateOf((document.asDynamic().visibilityState as String) == "visible")
        }
        DisposableEffect(Unit) {
            val listener: (org.w3c.dom.events.Event) -> Unit = {
                isAppInForeground = (document.asDynamic().visibilityState as String) == "visible"
            }
            document.addEventListener("visibilitychange", listener)
            onDispose { document.removeEventListener("visibilitychange", listener) }
        }
        TovTalkWithHebrewFont(debugFont = debugFont) {
            TovTalkApp(
                pendingDeepLink = pendingDeepLink,
                onDeepLinkConsumed = { pendingDeepLink = null },
                onShowError = { msg -> window.alert(msg) },
                onLanguageApplied = { },
                onOpenUrl = { url -> window.open(url, "_blank") },
                onPreloadNextVideoUrl = ::preloadNextVideoUrl,
                isAppInForeground = isAppInForeground,
                initialLanguage = lang,
                debugFont = debugFont,
            )
        }
    }
}
